/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Button, ButtonIcon, HelperText, InputBase, Label, Tooltip } from "../..";
import { getGlobal } from "../../../global";
import locale from "../../../locale";
import PropTypes from 'prop-types';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { render } from 'react-dom';
import { bgColor, borderRadius4px, cursorPointer, displayBlock, displayFlex, displayNone, flexRow, itemsCenter, justifyEnd, left, mg, parseMaxWidth, parseMinWidth, parseWidthHeight, positionFixed, scale, top, z } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { capitalizeSentenceCase, date as moment, randomString, updatePosition } from "../../../utils";
import CalendarComp from "../calendar";
import { isValidDate } from "../calendar/function";
import ControlComp from "../control";
const {
  zIndex: zIndexCORE
} = useTheme();
const confirmText = getGlobal(['confirm']);
const cancelText = getGlobal(['cancel']);
const formatValue = (value, format, utc = false) => {
  return moment(value).format(format, utc);
};
const DatePicker = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  actionIconAt,
  clearAble,
  controls,
  defaultValue,
  disabled,
  displayAnotherMonth,
  displayFormat,
  error,
  errorProps,
  inputProps,
  inputStyle,
  label,
  labelProps,
  max,
  min,
  onChange,
  placeholder,
  pressESCToClose,
  readOnly,
  required,
  returnFormat,
  style,
  textAlign,
  value: valueProp,
  viewType,
  ...props
}, reference) => {
  var _ipRef$current, _ipRef$current2;
  const [id] = useState(randomString(6, {
    allowSymbol: false
  }));
  const unique = {
    disabled: 'DGN-UI-DatePicker-disabled',
    error: 'DGN-UI-DatePicker-error',
    focus: 'DGN-UI-DatePicker-focus',
    footer: 'DGN-UI-DatePicker-Footer',
    cancel: 'DGN-UI-DatePicker-cancel',
    confirm: 'DGN-UI-DatePicker-confirm',
    backGr: 'DGN-UI-DatePicker-Background',
    icon: 'DGN-UI-DatePicker-Icon',
    picker: `DGN-UI-DatePicker-Picker-${id}`,
    portal: 'DGN-UI-Portal'
  };
  const calRef = useRef(null);
  const footerRef = useRef(null);
  const iconRef = useRef(null);
  const ipConRef = useRef(null);
  const ipRef = useRef(null);
  const ref = useRef(null);
  const displayValue = useRef();
  const focusedValue = useRef(null);
  const [, setDisplayValue] = useState(Date.now());
  const [, setFocusedValue] = useState(Date.now());
  const [value, setValue] = useState(valueProp || defaultValue);
  const _IconAreaCSS = IconAreaCSS(unique);
  const handleDisplayValue = useCallback(value => {
    displayValue.current = new Date(value);
    setDisplayValue();
  }, [displayValue]);
  const handleFocusedValue = useCallback(value => {
    focusedValue.current = new Date(value);
    setFocusedValue();
  }, [focusedValue]);
  const setButtonState = () => {
    if (!footerRef.current || focusedValue.current === undefined || !isValidDate(focusedValue.current)) return;
    const isEqual = focusedValue.current === null || Date.parse(new Date(focusedValue.current)) === Date.parse(new Date(displayValue.current));
    footerRef.current.querySelector(`.${unique.cancel}`).disabled = isEqual;
    footerRef.current.querySelector(`.${unique.confirm}`).disabled = isEqual;
  };
  const onConfirm = () => {
    displayValue.current = focusedValue.current;
    setButtonState();
    onChangeValue({
      value: focusedValue.current
    });
    closePicker();
  };
  const onCancel = () => {
    focusedValue.current = null;
    setButtonState();
    closePicker();
  };
  const setInputValue = e => {
    ipRef.current.value = capitalizeSentenceCase(formatValue(e.value, displayFormat));
    if (clearAble) onSwap(false);
  };
  const onChangeValue = e => {
    if (valueProp) {
      closePicker();
    } else {
      setValue(e.value);
    }
    !!onChange && onChange({
      value: formatValue(e.value, returnFormat)
    });
  };
  const onCalendarClick = e => {
    handleFocusedValue(e.value);
    controls ? setButtonState() : onChangeValue(e);
  };
  const getPickerStyle = () => {
    const {
      innerHeight,
      innerWidth
    } = window;
    const {
      left,
      height,
      top,
      width
    } = ipConRef.current.getBoundingClientRect();
    const pickerHeight = controls ? 310 : 256;
    let style = {};
    style.top = top + height + 4;
    style.transformOrigin = '50% 20%';
    if (style.top + pickerHeight > innerHeight) {
      if (top - pickerHeight > 0) {
        style.top = top - pickerHeight - 4;
        style.transformOrigin = '50% 80%';
      }
    }
    return PickerCSS.picker(style, left + 400 > innerWidth ? innerWidth - 400 : left, width).styles;
  };
  const createBackground = () => {
    let background = document.getElementById(unique.backGr);
    if (!background) {
      background = document.createElement('div');
      background.id = unique.backGr;
      background.classList.add(unique.portal, unique.backGr);
      document.body.appendChild(background);
    }
    background.style.cssText = PickerCSS.backGr;
    return background;
  };
  const createPicker = () => {
    const picker = document.createElement('div');
    picker.id = unique.picker;
    picker.style.cssText = getPickerStyle();
    return picker;
  };
  const pressESCHandler = event => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      onClickOutside({
        target: null
      });
    }
  };
  const onScroll = () => {
    const node = document.getElementById(unique.picker);
    if (!ipConRef.current || !node) {
      window.removeEventListener('scroll', onScroll);
      return;
    }
    updatePosition(ipConRef.current, node, closePicker);
  };
  const onResize = () => {
    if (!ref.current) return;
    const {
      width
    } = ref.current.getBoundingClientRect();
    const node = document.getElementById(unique.picker);
    if (node) {
      node.style.width = width + 'px';
    }
    onScroll();
  };
  const openPicker = () => {
    const backGr = createBackground();
    const picker = createPicker();
    render(jsx(CalendarComp, {
      ref: calRef,
      actions: controls && footerMemo,
      defaultValue: displayValue.current,
      value: focusedValue.current,
      max: max,
      min: min,
      displayAnotherMonth: displayAnotherMonth,
      onClick: onCalendarClick
    }), backGr.appendChild(picker));
    setTimeout(() => {
      Object.assign(picker.style, {
        ...PickerCSS.active
      });
      window.addEventListener('resize', onResize);
      window.addEventListener('scroll', onScroll);
      document.addEventListener('mousedown', onClickOutside);
      if (pressESCToClose) {
        document.addEventListener('keydown', pressESCHandler);
      }
      setButtonState();
    });
  };
  const closePicker = () => {
    const backGr = document.getElementById(unique.backGr);
    if (!backGr) return;
    const picker = backGr.querySelector(`#${unique.picker}`);
    if (!picker) return;
    Object.assign(picker.style, {
      ...PickerCSS.remove
    });
    if (pressESCToClose) {
      document.removeEventListener('keydown', pressESCHandler);
    }
    document.removeEventListener('mousedown', onClickOutside);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    setTimeout(() => {
      backGr.remove();
    }, 200);
    if (ipConRef.current) {
      ipConRef.current.classList.remove(unique.focus);
      ipConRef.current.style.zIndex = null;
    }
    // iconRef.current && onSwap(true);
    ipRef.current && ipRef.current.blur();
  };
  const onSwap = open => {
    const t = iconRef.current.childNodes[+open];
    if (open) {
      Object.assign(t.style, {
        ...hiddenStyle
      });
      Object.assign(t.previousSibling.style, {
        ...activeStyle
      });
      setTimeout(() => {
        t.previousSibling.style.pointerEvents = null;
      }, 200);
    } else {
      Object.assign(t.style, {
        ...hiddenStyle
      });
      Object.assign(t.nextSibling.style, {
        ...activeStyle
      });
      setTimeout(() => {
        t.nextSibling.style.pointerEvents = null;
      }, 200);
    }
  };
  const onInputFocus = () => {
    const el = ipConRef.current;
    if (!el.classList.contains(unique.focus)) {
      el.classList.add(unique.focus);
      el.style.zIndex = zIndexCORE(2);
      openPicker();
    }
  };
  const onClickOutside = e => {
    if (ipConRef.current && !ipConRef.current.contains(e.target) && ipRef.current && !ipRef.current.contains(e.target) && calRef.current && !calRef.current.contains(e.target)) {
      controls ? onCancel() : closePicker();
    }
  };
  const triggerFocus = () => {
    if (!ipConRef.current.classList.contains(unique.focus)) {
      ipRef.current.focus();
    } else {
      onClickOutside({
        target: null
      });
    }
  };
  const onClear = () => {
    if (!clearAble && !ipRef.current) return;
    ipRef.current.value = '';
    focusedValue.current = null;
    setButtonState();
    const e = {
      value: null
    };
    !!onChange && onChange(e);
    onSwap(true);
  };
  const iconComp = jsx("div", {
    css: _IconAreaCSS,
    ref: iconRef
  }, jsx(ButtonIcon, {
    disabled: disabled || readOnly,
    viewType: 'ghost',
    onClick: triggerFocus,
    className: `${unique.icon} ${unique.icon}-accept`,
    viewBox: true,
    name: 'Calendar'
  }), jsx(ButtonIcon, {
    disabled: disabled || readOnly,
    viewType: 'ghost',
    onClick: onClear,
    className: `${unique.icon} ${unique.icon}-cancel`,
    viewBox: true,
    name: 'Close'
  }));
  const endIcon = actionIconAt === 'end' && iconComp;
  const startIcon = actionIconAt === 'start' && iconComp;
  const footerMemo = jsx("div", {
    css: ControlContainerCSS,
    className: unique.footer,
    ref: footerRef
  }, jsx(Button, {
    className: unique.cancel,
    onClick: onCancel
  }, cancelText), jsx(Button, {
    color: 'info',
    className: unique.confirm,
    onClick: onConfirm
  }, confirmText));
  const isError = !!error && (!value || !isValidDate(value));
  useEffect(() => {
    if (defaultValue && isValidDate(defaultValue)) {
      handleDisplayValue(defaultValue);
      setInputValue({
        value: displayValue.current
      });
    }
    return () => {
      closePicker();
    };
  }, []);
  useEffect(() => {
    if (value && isValidDate(value)) {
      handleFocusedValue(value);
      handleDisplayValue(value);
      setInputValue({
        value: displayValue.current
      });
      setButtonState();
      closePicker();
    } else if (defaultValue && isValidDate(defaultValue)) {
      handleFocusedValue(defaultValue);
      handleDisplayValue(defaultValue);
      setInputValue({
        value: displayValue.current
      });
      setButtonState();
      closePicker();
    } else {
      if (ipRef.current) ipRef.current.value = '';
      if (clearAble) {
        onSwap(true);
      }
      setButtonState();
      closePicker();
    }
  }, [value]);
  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);
  useEffect(() => {
    if (!readOnly) {
      ipRef.current.addEventListener('focus', onInputFocus);
    }
    return () => {
      if (!ipRef.current) return;
      if (!readOnly) {
        ipRef.current.removeEventListener('focus', onInputFocus);
      }
    };
  }, [onChange]);
  useEffect(() => {
    if (ipRef.current.value !== '') {
      const e = {
        value: focusedValue.current || displayValue.current
      };
      setInputValue(e, displayFormat);
    }
    if (disabled) {
      iconRef.current.childNodes[0].style.pointerEvents = 'none';
      closePicker();
    } else if (!readOnly) {
      ipRef.current.addEventListener('focus', onInputFocus);
    } else {
      iconRef.current.childNodes[0].style.pointerEvents = 'none';
      closePicker();
    }
    return () => {
      if (!iconRef.current || !ipRef.current) return;
      if (disabled) {
        iconRef.current.childNodes[0].style.pointerEvents = null;
      } else if (!readOnly) {
        ipRef.current.removeEventListener('focus', onInputFocus);
      } else {
        iconRef.current.childNodes[0].style.pointerEvents = null;
      }
      closePicker();
    };
  }, [actionIconAt, clearAble, controls, disabled, displayFormat, displayAnotherMonth, max, min, readOnly, returnFormat, viewType]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {
      ...action,
      get: () => {
        return ref.current;
      },
      getInputContainer: () => {
        return ipConRef.current;
      },
      getInput: () => {
        return ipRef.current;
      },
      getCalendar: () => {
        return calRef.current;
      },
      getIcon: () => {
        return iconRef.current;
      }
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.get = () => ref.current;
    currentRef.getInputContainer = () => ipConRef.current;
    currentRef.getInput = () => ipRef.current;
    currentRef.getCalendar = () => calRef.current;
    currentRef.getIcon = () => iconRef.current;
    return currentRef;
  });
  return jsx(ControlComp, {
    ...props,
    disabled: disabled,
    error: isError,
    ref: ref,
    style: style
  }, label ? jsx(Label, {
    disabled: disabled,
    required: required,
    ...labelProps
  }, label) : null, jsx(Tooltip, {
    title: (ipRef === null || ipRef === void 0 ? void 0 : (_ipRef$current = ipRef.current) === null || _ipRef$current === void 0 ? void 0 : _ipRef$current.clientWidth) < 200 ? ipRef === null || ipRef === void 0 ? void 0 : (_ipRef$current2 = ipRef.current) === null || _ipRef$current2 === void 0 ? void 0 : _ipRef$current2.value : ''
  }, jsx(InputBase, {
    inputProps: {
      placeholder: !readOnly && !disabled ? placeholder : '',
      ...inputProps
    },
    ref: ipConRef,
    inputRef: ipRef,
    disabled: disabled,
    viewType: viewType,
    inputStyle: {
      textAlign: textAlign,
      ...inputStyle
    },
    startIcon: startIcon,
    endIcon: endIcon,
    onKeyDown: e => e.preventDefault(),
    readOnly: true
  })), isError ? jsx(HelperText, {
    ...errorProps,
    style: {
      minHeight: 16,
      position: 'absolute',
      top: '100%'
    },
    disabled: disabled
  }, error) : null);
}));
const hiddenStyle = {
  opacity: 0,
  display: 'none'
};
const activeStyle = {
  opacity: 1,
  pointerEvents: 'none',
  display: 'block'
};
const IconAreaCSS = unique => css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    color: inherit;
    .${unique.icon} {
        ${displayNone};
        ${cursorPointer};
        opacity: 0;
        transition: display 50ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, opacity 300ms cubic-bezier(0.4, 0, 0.2, 1) 50ms;
        will-change: display, opacity;
    }
    .${unique.icon}-accept {
        ${displayBlock};
        opacity: 1;
    }
`;
const ControlContainerCSS = css`
    ${displayFlex};
    ${flexRow};
    ${justifyEnd};
    ${mg([0, 4, 4])};
`;
const PickerCSS = {
  backGr: `background-color: transparent; inset: 0px; pointer-events: auto; position: fixed; z-index: ${zIndexCORE(1)};`,
  picker: (position, positionLeft, width) => css`
        ${positionFixed};
        ${borderRadius4px};
        ${bgColor('system.white')};
        ${parseWidthHeight(width, 'max-content')};
        ${parseMinWidth(window.innerWidth <= 599 ? 280 : 314)};
        ${parseMaxWidth(400)};
        ${scale(0)};
        ${left(positionLeft)};
        ${top(position.top)};
        transform-origin: ${position.transformOrigin};
        opacity: 0;
        transform: scale(0);
        transition: opacity 120ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        ${z(2)};
    `,
  active: {
    opacity: 1,
    transform: 'scale(1)'
  },
  remove: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'scale(0)'
  }
};
DatePicker.defaultProps = {
  actionIconAt: 'end',
  clearAble: false,
  controls: false,
  disabled: false,
  displayAnotherMonth: true,
  displayFormat: locale.get() === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY',
  pressESCToClose: true,
  readOnly: false,
  required: false,
  returnFormat: 'YYYY-MM-DD',
  textAlign: 'start',
  viewType: 'underlined'
};
DatePicker.propTypes = {
  /** Action icons position. */
  actionIconAt: PropTypes.oneOf(['end', 'start']),
  /** Display the clear icon if true. */
  clearAble: PropTypes.bool,
  /** If `true`, the calendar will have a footer controls. */
  controls: PropTypes.bool,
  /** The default value of the component. */
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.object]),
  /** If `true`, the form control will be disabled. */
  disabled: PropTypes.bool,
  /** Format to display value. */
  displayFormat: PropTypes.oneOfType([PropTypes.oneOf(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD']), PropTypes.string]),
  /** View days of previous or next month. */
  displayAnotherMonth: PropTypes.bool,
  /** Error of the form control. */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** InputProps are properties of the input element. */
  inputProps: PropTypes.object,
  /** InputStyle is style of input element. */
  inputStyle: PropTypes.object,
  /** Content of the label element. */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** LabelProps are properties if the label element. */
  labelProps: PropTypes.object,
  /** Min value of date picker. */
  min: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.object]),
  /** Max value of date picker. */
  max: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.object]),
  /** A callback function when value input change. */
  onChange: PropTypes.func,
  /** Hints for input. */
  placeholder: PropTypes.string,
  /** Allow close Popup when press ESC. */
  pressESCToClose: PropTypes.bool,
  /** Status read only of input. */
  readOnly: PropTypes.bool,
  /** Required state of the form control. */
  required: PropTypes.bool,
  /** Format of the returned value. */
  returnFormat: PropTypes.oneOfType([PropTypes.oneOf(['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD']), PropTypes.string]),
  /** Style inline of component. */
  style: PropTypes.object,
  /** Text align of the input. */
  textAlign: PropTypes.oneOf(['center', 'end', 'start']),
  /** Value of the component. */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.array, PropTypes.object]),
  /** View type of the form control. */
  viewType: PropTypes.oneOf(['outlined', 'underlined'])
};
export default DatePicker;